/**
 * Stub synthesis — the structural half of incomplete-by-design (v0.6,
 * SPECIFICATION.md §4.13).
 *
 * A process step always names a caller module and a callee interface, so a
 * process *fully implies* the structural elements it touches. When those
 * elements are referenced before they're defined, we synthesize **default
 * stubs** for them — so the skeleton draws (edges form, nodes render dashed)
 * instead of the reference dangling. This is what makes process-first authoring
 * work: write the flow, the modules/interfaces appear.
 *
 * Diagnostics are NOT produced here. Validation (which runs over the
 * *non-stubbed* model) already classifies every undefined reference as a `todo`
 * (and role conflicts as `error`) — see validation.ts. Synthesis only augments
 * the model the graph + renderer consume; the stub set is exactly the local
 * undefined-reference set that validation reports as TODO.
 *
 * Scope (v0.6): module + interface stubs (the visible skeleton). Forward-
 * referenced subprocesses surface as a `todo` from validation but synthesize no
 * node — `do` steps contribute no dependency-graph edge, so there's nothing to
 * draw.
 */

import {
  EMPTY_BODY,
  EMPTY_INTERFACE_BODY,
  ANONYMOUS_INTERFACE_NAME,
  type OnInclusion,
  type SourceSpan,
} from "./types.js";
import type { Flow, ResolvedCallee } from "./process-model.js";
import { forEachCallHop, forEachNode, collectBindNames } from "./process-ir-walk.js";
import type {
  ResolvedInterface,
  ResolvedModel,
  ResolvedModule,
} from "./resolver.js";
import { buildSymbolSets, resolveFlatModuleRef, spaceOfModule } from "./hoist-index.js";

export interface StubResult {
  readonly model: ResolvedModel;
  /** Canonical dotted paths of every synthesized module stub. */
  readonly modulePaths: readonly string[];
  /** Canonical dotted paths of every synthesized interface stub. */
  readonly interfacePaths: readonly string[];
}

/**
 * A synthesized stub has no declaration text, so it owns no real source span —
 * but it WAS implied by a process step, and that step lives in a file. We stamp
 * the stub's decl with a zero-position span carrying that file so every
 * synthesized symbol still reports the file it was generated from (board→editor
 * reveal scopes by it; `serialize` reads `decl.source.file`). The position is a
 * placeholder — consumers re-locate the reference line in text.
 */
const ZERO: SourceSpan["start"] = { line: 1, column: 1, offset: 0 };
function originSpan(file: string | undefined): SourceSpan | undefined {
  return file === undefined ? undefined : { start: ZERO, end: ZERO, file };
}

/**
 * Augment a resolved model with stub modules/interfaces for every local
 * reference a process makes to a not-yet-defined element. Pure: returns a new
 * model; the input is untouched. When nothing is missing, returns the input
 * model unchanged (referentially equal) so callers can cheaply detect a no-op.
 */
export function synthesizeStubs(model: ResolvedModel): StubResult {
  if (
    model.processes.length === 0 &&
    model.subprocesses.length === 0 &&
    (model.onInclusions?.length ?? 0) === 0
  ) {
    return { model, modulePaths: [], interfacePaths: [] };
  }

  const known = buildKnown(model);
  const conflicts = findProcessRoleConflicts(model);
  const declared = buildDeclaredModuleIndex(model);

  // Collect the references that need stubbing.
  const neededModules = new Set<string>(); // canonical module paths to ensure
  const neededIfaces = new Set<string>(); // canonical interface paths to ensure
  // path → file of the process step that first implied this stub. Ancestors are
  // recorded too (a stubbed parent inherits the child reference's file).
  const originByPath = new Map<string, string>();

  const collectFrom = (
    body: Flow | undefined,
    file: string | undefined,
    ownerPrefix: readonly string[],
    paramBind?: string
  ): void => {
    const binds = collectBindNames(body);
    // The `on Caller` param is a template placeholder bound per `do` site — not a
    // real module — so seed it as a bind to keep it from stubbing a phantom module.
    if (paramBind) binds.add(paramBind);
    collectRefs(body, binds, known, conflicts, declared, ownerPrefix, neededModules, neededIfaces, originByPath, file);
  };
  // Read the resolved IR (processModels + subprocessModels) — the latter now
  // includes process-nested subprocesses, so one pass covers everything the old
  // process/nested/top-level AST loops did.
  //
  // §4.11 point 5: an unresolved reference stubs in the REFERRER's scope. For a
  // process owned by a module that scope is the owning module — `ownerPath`
  // becomes the placement prefix for anything the process implies that resolves
  // nowhere. A process-nested subprocess (no ownerPath of its own) inherits its
  // host process's owner via the `ownerProcess` identity.
  const ownerByProcessIdentity = new Map<string, readonly string[]>();
  for (const pm of model.processModels ?? []) {
    if (!pm.ownerPath || pm.ownerPath.length === 0) continue;
    const qualified = `${pm.ownerPath.join(".")}.${pm.name}`;
    // Must match the builder's `ownerProcess` minting byte-for-byte (id, else
    // the space-qualified name — process-model-builder.ts).
    const identity =
      pm.id ?? (pm.space && pm.space.length > 0 ? `${pm.space.join(".")}::${qualified}` : qualified);
    ownerByProcessIdentity.set(identity, pm.ownerPath);
  }
  for (const pm of model.processModels ?? []) collectFrom(pm.body, pm.source?.file, pm.ownerPath ?? []);
  for (const sm of model.subprocessModels ?? []) {
    const ownerPrefix =
      sm.ownerPath ?? (sm.ownerProcess ? ownerByProcessIdentity.get(sm.ownerProcess) : undefined) ?? [];
    collectFrom(sm.body, sm.source?.file, ownerPrefix, sm.templateParam?.name);
  }

  // §1.2: an inclusion naming a module that isn't declared anywhere is the same
  // open-world draft as a forward-referenced callee — stub the ghost so the
  // plane it was included on actually draws something.
  for (const inc of unresolvedInclusions(model)) {
    const key = inc.target.join(".");
    neededModules.add(key);
    const file = inc.source?.file;
    if (file !== undefined) {
      for (let i = 1; i <= inc.target.length; i++) {
        const prefix = inc.target.slice(0, i).join(".");
        if (!originByPath.has(prefix)) originByPath.set(prefix, file);
      }
    }
  }

  if (neededModules.size === 0 && neededIfaces.size === 0) {
    return { model, modulePaths: [], interfacePaths: [] };
  }

  // Build the additions trie (segments from root). Every needed module path and
  // every needed interface's owner path is materialized as trie nodes; ancestors
  // are created implicitly by walking the segments.
  const root: TrieNode = { children: new Map(), ifaces: new Set() };
  const modulePaths: string[] = [];
  const interfacePaths: string[] = [];

  for (const path of neededModules) {
    ensureModuleNode(root, path.split("."));
  }
  for (const ipath of neededIfaces) {
    const segs = ipath.split(".");
    if (segs.length < 2) continue; // an interface must live inside a module
    const ownerSegs = segs.slice(0, -1);
    const leaf = segs[segs.length - 1]!;
    const ownerNode = ensureModuleNode(root, ownerSegs);
    ownerNode.ifaces.add(leaf);
  }

  const newModules = rebuildForest(model.modules, root, [], known, modulePaths, interfacePaths, originByPath);

  if (modulePaths.length === 0 && interfacePaths.length === 0) {
    return { model, modulePaths: [], interfacePaths: [] };
  }
  return {
    model: { ...model, modules: newModules },
    modulePaths,
    interfacePaths,
  };
}

/**
 * v0.9.0 §1.2: the inclusion targets that name no declared module. Shared by
 * synthesis (which stubs the ghost) and validation (which reports the `TODO`)
 * so the two can never disagree about what "unresolved" means.
 *
 * Resolution mirrors hoisting: an exact qualified match, else the unique
 * last-segment candidate, else the unique suffix match. Ambiguity counts as
 * unresolved — no stub, and the ambiguity's own diagnostic owns it.
 */
export function unresolvedInclusions(model: ResolvedModel): readonly OnInclusion[] {
  const incls = model.onInclusions;
  if (!incls || incls.length === 0) return [];
  const { known, byLast } = buildDeclaredModuleIndex(model);
  // The SAME rule the presence pass applies — shared so a target one pass
  // resolves can never be a ghost to the other. Deliberately no alias consult:
  // presence resolves the bare written form only, and the two must agree.
  return incls.filter((inc) => resolveFlatModuleRef(inc.target, known, byLast) === undefined);
}

/**
 * Canonical index of DECLARED modules (stubs are never resolution candidates,
 * §4.12): every canonical dotted path, a last-segment candidate list for the
 * hoisting-lite ladder, and the v0.11 D3 space-extended spelling of each path
 * (`Space.Full.Path` → canonical) — the written form `buildSymbolSets` admits
 * as module membership, which must therefore canonicalize instead of stubbing.
 */
interface DeclaredModuleIndex {
  readonly known: ReadonlySet<string>;
  readonly byLast: ReadonlyMap<string, readonly string[]>;
  readonly aliases: ReadonlyMap<string, string>;
}

function buildDeclaredModuleIndex(model: ResolvedModel): DeclaredModuleIndex {
  const known = new Set<string>();
  const byLast = new Map<string, string[]>();
  const aliases = new Map<string, string>();
  const spaces = model.spaces;
  const indexMod = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    const key = path.join(".");
    known.add(key);
    (byLast.get(m.name) ?? byLast.set(m.name, []).get(m.name)!).push(key);
    const space = spaceOfModule(m, spaces);
    if (space && space.length > 0) aliases.set([...space, ...path].join("."), key);
    for (const c of m.modules) indexMod(c, path);
  };
  for (const m of model.modules) indexMod(m, []);
  return { known, byLast, aliases };
}

/**
 * Cross-step role conflicts (§10.2): a name used as an **interface leaf** (a
 * callee terminal) in one place and as a **module** (a caller, or an owning
 * prefix of some callee — "it has something inside") in another, while undefined
 * in both. These cannot both be true, so the name is a contradiction, not a
 * draft — validation reports it as an `error` and synthesis refuses to stub it.
 *
 * Returns the set of conflicting paths. Shared by synthesis (skip them) and
 * validation (error on them) so the two never disagree.
 */
export function findProcessRoleConflicts(model: ResolvedModel): ReadonlySet<string> {
  const known = buildKnown(model);
  const moduleRole = new Set<string>(); // undefined callers + undefined-callee prefixes
  const ifaceLeaf = new Set<string>(); // undefined callee terminals

  const visit = (body: Flow | undefined, binds: ReadonlySet<string>): void => {
    forEachCallHop(body, (hop) => {
      // Caller. An anonymous head (`caller === []`) names no role.
      const fromKey = hop.caller.join(".");
      if (hop.caller.length > 0 && !known.modules.has(fromKey) && !binds.has(fromKey) && !known.interfaces.has(fromKey)) {
        moduleRole.add(fromKey);
      }
      // Callee, by the IR's resolved classification (§4.13/§10.3). A callee that
      // deduced a MODULE (`anonymous` → its handler is the synthesized
      // `(anonymous)` leaf, never a conflict source) takes the module role; a
      // real interface-leaf terminal whose leaf isn't yet defined is an
      // interface role, with every prefix a module.
      const callee = hop.callee;
      if (callee.anonymous) {
        const mkey = callee.modulePath.join(".");
        if (!known.modules.has(mkey) && !known.interfaces.has(mkey)) moduleRole.add(mkey);
        for (let i = 1; i < callee.modulePath.length; i++) moduleRole.add(callee.modulePath.slice(0, i).join("."));
      } else {
        const ikey = callee.interfacePath.join(".");
        if (!known.interfaces.has(ikey) && !known.modules.has(ikey)) {
          ifaceLeaf.add(ikey);
          for (let i = 1; i < callee.interfacePath.length; i++) moduleRole.add(callee.interfacePath.slice(0, i).join("."));
        }
      }
    });
  };
  for (const pm of model.processModels ?? []) visit(pm.body, collectBindNames(pm.body));
  for (const sm of model.subprocessModels ?? []) {
    const binds = collectBindNames(sm.body);
    if (sm.templateParam) binds.add(sm.templateParam.name);
    visit(sm.body, binds);
  }

  const conflicts = new Set<string>();
  for (const name of ifaceLeaf) if (moduleRole.has(name)) conflicts.add(name);
  return conflicts;
}

// =============================================================================
// Known-symbol index (mirrors validation's buildSymbolIndex membership rules)
// =============================================================================

interface Known {
  /** Canonical + short module names (a deep module is reachable by short name,
   *  matching validation/graph). */
  readonly modules: ReadonlySet<string>;
  /** Canonical interface paths only. */
  readonly interfaces: ReadonlySet<string>;
}

function buildKnown(model: ResolvedModel): Known {
  // Membership only — every hoistable module/interface suffix. Shared with the
  // validator via `buildSymbolSets` so classification and validation agree on
  // what a reference names (the graph decides which suffix uniquely resolves).
  return buildSymbolSets(model);
}

// =============================================================================
// Reference collection
// =============================================================================

/** Collect the module/interface stubs a process flow IMPLIES, reading the
 *  resolved IR (`ResolvedCallee` is `classifyCallee`'s verdict, already computed
 *  at build time — so the stub set is identical to the old AST walk, with no
 *  re-classification). Every call hop's caller + callee, plus a `do`-site
 *  template caller, is considered. */
function collectRefs(
  body: Flow | undefined,
  binds: ReadonlySet<string>,
  known: Known,
  conflicts: ReadonlySet<string>,
  declared: DeclaredModuleIndex,
  ownerPrefix: readonly string[],
  neededModules: Set<string>,
  neededIfaces: Set<string>,
  originByPath: Map<string, string>,
  file: string | undefined
): void {
  // Record the origin file for a path and every module-ancestor prefix, first
  // reference winning (deterministic by iteration order). No-op when the flow's
  // process has no file (legacy/synthetic source).
  const recordOrigin = (segs: readonly string[]): void => {
    if (file === undefined) return;
    for (let i = 1; i <= segs.length; i++) {
      const key = segs.slice(0, i).join(".");
      if (!originByPath.has(key)) originByPath.set(key, file);
    }
  };
  // Canonicalize a WRITTEN module path before materializing anything at it:
  // the longest resolvable head (exact / D3 extended alias / unique
  // last-segment / unique suffix — the same ladder references resolve by)
  // grafts the unresolved tail under the declared module, so a hoisted
  // spelling can never mint a root-level duplicate of an existing nested
  // module. A path that resolves nowhere places under the referrer's owning
  // module (§4.11 point 5) — the package root for free-standing processes.
  const canonicalize = (segs: readonly string[]): readonly string[] => {
    for (let i = segs.length; i >= 1; i--) {
      const head = segs.slice(0, i);
      const hit =
        declared.aliases.get(head.join(".")) ?? resolveFlatModuleRef(head, declared.known, declared.byLast);
      if (hit !== undefined) return [...hit.split("."), ...segs.slice(i)];
    }
    return [...ownerPrefix, ...segs];
  };
  const considerCaller = (fromKey: string): void => {
    // An anonymous head (`caller === []`) that survived implicit-caller fill is a
    // free-standing process — validation errors, no module to stub.
    if (fromKey === "") return;
    // Defined, bound, or a role conflict → not a stub.
    if (known.modules.has(fromKey)) return;
    if (binds.has(fromKey)) return;
    if (known.interfaces.has(fromKey)) return; // declared interface used as caller — validation errors
    if (conflicts.has(fromKey)) return; // cross-step role conflict — validation errors
    const segs = canonicalize(fromKey.split("."));
    neededModules.add(segs.join("."));
    recordOrigin(segs);
  };
  const considerCallee = (callee: ResolvedCallee): void => {
    // Conflict key matches the old written-callee key: a module for an anonymous
    // callee (trailing dot / bare module), the interface leaf otherwise.
    const conflictKey = (callee.anonymous ? callee.modulePath : callee.interfacePath).join(".");
    if (conflictKey && conflicts.has(conflictKey)) return; // role conflict — validation errors
    const ipath = callee.interfacePath.join(".");
    if (!callee.anonymous && known.interfaces.has(ipath)) return; // declared leaf — nothing to stub
    // An anonymous callee always stubs its `(anonymous)` handler leaf (the leaf
    // name self-marks it); an undeclared real leaf stubs onto its owner. Both
    // land on the CANONICAL owner path.
    const owner = canonicalize(callee.interfacePath.slice(0, -1));
    const leaf = callee.interfacePath[callee.interfacePath.length - 1]!;
    const segs = [...owner, leaf];
    neededIfaces.add(segs.join("."));
    recordOrigin(segs);
  };

  forEachCallHop(body, (hop) => {
    considerCaller(hop.caller.join("."));
    considerCallee(hop.callee);
  });
  forEachNode(body, (n) => {
    // `Orders do X` — the do-site template caller performs X's inlined steps, so
    // stub it as a module.
    if (n.kind === "splice" && n.templateCaller?.form === "explicit" && n.templateCaller.resolved) {
      considerCaller(n.templateCaller.resolved.join("."));
    }
  });
}

// =============================================================================
// Additions trie + immutable forest rebuild
// =============================================================================

interface TrieNode {
  readonly children: Map<string, TrieNode>;
  /** Interface leaf names to ensure on the module at this node. */
  readonly ifaces: Set<string>;
}

function ensureModuleNode(root: TrieNode, segs: readonly string[]): TrieNode {
  let node = root;
  for (const seg of segs) {
    let child = node.children.get(seg);
    if (!child) {
      child = { children: new Map(), ifaces: new Set() };
      node.children.set(seg, child);
    }
    node = child;
  }
  return node;
}

/**
 * Rebuild a module forest, merging the additions trie. Existing modules are
 * cloned only along paths that gain children/interfaces; untouched subtrees are
 * returned by reference. New modules/interfaces are marked `synthesized`.
 */
function rebuildForest(
  existing: readonly ResolvedModule[],
  trie: TrieNode,
  parentSegs: readonly string[],
  known: Known,
  outModulePaths: string[],
  outInterfacePaths: string[],
  originByPath: ReadonlyMap<string, string>
): readonly ResolvedModule[] {
  if (trie.children.size === 0) return existing;

  const byName = new Map<string, ResolvedModule>();
  for (const m of existing) byName.set(m.name, m);

  const result: ResolvedModule[] = [];
  const handled = new Set<string>();

  for (const m of existing) {
    const sub = trie.children.get(m.name);
    if (sub) {
      handled.add(m.name);
      result.push(
        augmentModule(m, sub, [...parentSegs, m.name], known, outModulePaths, outInterfacePaths, originByPath)
      );
    } else {
      result.push(m);
    }
  }
  // Trie children with no existing module → brand-new stub modules.
  for (const [name, sub] of trie.children) {
    if (handled.has(name) || byName.has(name)) continue;
    result.push(
      createStubModule(name, sub, [...parentSegs, name], outModulePaths, outInterfacePaths, originByPath)
    );
  }
  return result;
}

/** Clone an existing module, adding only the new sub-modules / interfaces the
 *  trie demands. The module itself keeps `synthesized` unset (it was authored). */
function augmentModule(
  m: ResolvedModule,
  trie: TrieNode,
  segs: readonly string[],
  known: Known,
  outModulePaths: string[],
  outInterfacePaths: string[],
  originByPath: ReadonlyMap<string, string>
): ResolvedModule {
  const modules = rebuildForest(
    m.modules,
    trie,
    segs,
    known,
    outModulePaths,
    outInterfacePaths,
    originByPath
  );

  const existingIfaceNames = new Set(m.interfaces.map((i) => i.name));
  const addedIfaces: ResolvedInterface[] = [];
  for (const leaf of trie.ifaces) {
    const path = `${segs.join(".")}.${leaf}`;
    // Skip if already present on this module, or anywhere (it may live in a
    // surface under a different path — but the literal ref path is what matters,
    // and that is `segs.leaf`, checked via existingIfaceNames + known).
    if (existingIfaceNames.has(leaf)) continue;
    if (known.interfaces.has(path)) continue;
    addedIfaces.push(makeStubInterface(leaf, originByPath.get(path)));
    outInterfacePaths.push(path);
  }

  if (modules === m.modules && addedIfaces.length === 0) return m;
  return {
    ...m,
    modules,
    interfaces: addedIfaces.length > 0 ? [...m.interfaces, ...addedIfaces] : m.interfaces,
  };
}

function createStubModule(
  name: string,
  trie: TrieNode,
  segs: readonly string[],
  outModulePaths: string[],
  outInterfacePaths: string[],
  originByPath: ReadonlyMap<string, string>
): ResolvedModule {
  const path = segs.join(".");
  outModulePaths.push(path);

  const modules: ResolvedModule[] = [];
  for (const [childName, sub] of trie.children) {
    modules.push(
      createStubModule(childName, sub, [...segs, childName], outModulePaths, outInterfacePaths, originByPath)
    );
  }
  const interfaces: ResolvedInterface[] = [];
  for (const leaf of trie.ifaces) {
    interfaces.push(makeStubInterface(leaf, originByPath.get(`${path}.${leaf}`)));
    outInterfacePaths.push(`${path}.${leaf}`);
  }

  const source = originSpan(originByPath.get(path));
  return {
    decl: { typeName: "module", name, body: EMPTY_BODY, ...(source ? { source } : {}) },
    name,
    typeName: "module",
    baseType: "module",
    body: EMPTY_BODY,
    surfaces: [],
    interfaces,
    modules,
    synthesized: true,
  };
}

function makeStubInterface(name: string, file: string | undefined): ResolvedInterface {
  const anonymous = name === ANONYMOUS_INTERFACE_NAME;
  const source = originSpan(file);
  return {
    decl: { typeName: "interface", name, body: EMPTY_INTERFACE_BODY, ...(source ? { source } : {}) },
    name,
    typeName: "interface",
    body: EMPTY_INTERFACE_BODY,
    synthesized: true,
    ...(anonymous ? { anonymous: true } : {}),
  };
}
